import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  Grid,
  MenuItem,
} from "@mui/material";
import * as Yup from "yup";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { useFormik } from "formik";
import BasicModal from "../../components/BasicModal";
import FormModalActions from "../../components/FormModalActions";
import { COMPACT_FORM_MODAL_SX } from "../../utils/formModalStyles";
import { getAllDepartment } from "../../services/departmentService";
import { saveAssignment, getAssignmentByAdmissionId, updateAssignment } from "../../services/PatientAssignmentService";
import { syncPatientTypeForWorkflow } from "../../services/patientService";
import { getAdmissionByPatientId } from "../../services/PatientAdmissionService";
import { getDoctorByDepartmentId, getDoctorsByDepartmentIdMultiple } from "../../services/doctorService";
import { getAllWards } from "../../services/WardService";
import { getBedsByWard, updateBed } from "../../services/BedService";
import { getAllStaff } from "../../services/StaffService";
import { filterDepartmentsForIpdWorkflow } from "../../utils/subscriptionScopeUtils";

// const validationSchema = Yup.object({
//   departmentId: Yup.number().required("Department is required"),
//   doctorId: Yup.number().required("Doctor is required"),
//   wardId: Yup.number().required("Ward is required"),
//   bedId: Yup.number().required("Bed is required"),
//   nurseId: Yup.array()
//     .min(1, "At least one staff must be assigned")
//     .required("Staff assignment is required"),
//   admissionDate: Yup.date()
//     .nullable()
//     .required("Assignment date is required"),
// });

const validationSchema = (isStaff) =>
  Yup.object({
    departmentId: Yup.number().required("Department is required"),

    doctorId: isStaff
      ? Yup.number().nullable() // ✅ NOT required for staff
      : Yup.number().required("Doctor is required"),

    wardId: Yup.number().required("Ward is required"),
    bedId: Yup.number().required("Bed is required"),

    nurseId: Yup.array()
      .min(1, "At least one staff must be assigned")
      .required("Staff assignment is required"),

    admissionDate: Yup.date()
      .nullable()
      .required("Assignment date is required"),
  });

const AssignPatientForm = ({ open, onClose, admissionId, patientId, patient_info = {} }) => {
   const user = JSON.parse(localStorage.getItem("user"));
  const isStaff = user?.roleName === "staff";

  const [departmentList, setDepartmentList] = useState([]);
  const [doctorList, setDoctorList] = useState([]); 
  const [wardList, setWardList] = useState([]);
  const [bedList, setBedList] = useState([]);
  const [assignment_info, setAssignmentInfo] = useState(null);
  const [staffList, setStaffList] = useState([]);
  const [filteredStaffList, setFilteredStaffList] = useState([]);

  const visibleDepartments = useMemo(
    () =>
      filterDepartmentsForIpdWorkflow(departmentList, {
        keepIds: [assignment_info?.departmentId],
      }),
    [departmentList, assignment_info?.departmentId]
  );

  useEffect(() => {
    if (open) {
      fetchInitialData();
      fetchPatientAssignemnt();
    }
    // } else {
    //   setFilteredStaffList([]);
    //   setDoctorList([]);
    //   setBedList([]);
    // }
  }, [open]);

  const fetchPatientAssignemnt = async () => {
    try {
      const patientAssignment = await getAssignmentByAdmissionId(admissionId);
      // Handle both array and single object responses
      let assignment = null;
      if (Array.isArray(patientAssignment)) {
        // If array, get the most recent assignment (highest ID or latest createdAt)
        if (patientAssignment.length > 0) {
          const sorted = [...patientAssignment].sort((a, b) => {
            if (a.createdAt && b.createdAt) {
              return new Date(b.createdAt) - new Date(a.createdAt);
            }
            return (b.id || 0) - (a.id || 0);
          });
          assignment = sorted[0];
        }
      } else if (patientAssignment) {
        assignment = patientAssignment;
      }
      setAssignmentInfo(assignment || null);
    } catch (error) {
      console.error("Failed to fetch assignment", error);
      setAssignmentInfo(null);
    }
  };

  useEffect(() => {
    if (assignment_info && assignment_info.departmentId) {
      console.log("Assignment info found, fetching doctors for department:", assignment_info.departmentId);
      fetchDoctorsForDepartment(assignment_info.departmentId);
      
      // Filter staff for the department
      const filtered = staffList.filter(
        (staff) => staff.departmentId === assignment_info.departmentId
      );
      console.log("Filtered staff for department:", filtered);
      setFilteredStaffList(filtered);
    }

    if (assignment_info?.wardId) {
      console.log("Fetching beds for ward:", assignment_info.wardId);
      fetchBedsForWard(assignment_info.wardId);
    }
  }, [assignment_info, staffList]);

  // Separate useEffect to handle initial data loading when assignment_info is available
  useEffect(() => {
    if (assignment_info && Object.keys(assignment_info).length > 0) {
      console.log("Assignment info loaded, initializing form data:", assignment_info);
      
      // Ensure doctors are fetched for the department
      if (assignment_info.departmentId) {
        fetchDoctorsForDepartment(assignment_info.departmentId);
      }
      
      // Ensure beds are fetched for the ward
      if (assignment_info.wardId) {
        fetchBedsForWard(assignment_info.wardId);
      }
    }
  }, [assignment_info]);


  const fetchInitialData = async () => {
    try {
      const [departments, wards, staff] = await Promise.all([
        getAllDepartment(),
        getAllWards(),
        getAllStaff(),
      ]);
      setDepartmentList(departments);
      setWardList(wards);
      setStaffList(staff);
    } catch (error) {
      console.error("Error loading initial data:", error);
    }
  };

  // const fetchBedsForWard = async (wardId) => {
  //   try {
  //     const beds = await getBedsByWard(wardId);
  //     const availableBeds = beds.filter((bed) => bed.isOccupied === false);
  //     setBedList(availableBeds);
  //   } catch (error) {
  //     console.error("Failed to fetch beds", error);
  //     setBedList([]);
  //   }
  // };

  const fetchBedsForWard = async (wardId) => {
    try {
      const beds = await getBedsByWard(wardId);

      // Step 1: Filter only available beds
      let availableBeds = beds.filter((bed) => bed.isOccupied === false);

      // Step 2: Only include the currently assigned bed if it belongs to THIS admission
      // This prevents showing beds assigned to other patients
      if (assignment_info?.bedId && assignment_info?.admissionId && admissionId) {
        // Only include if the bed is assigned to the current admission
        if (Number(assignment_info.admissionId) === Number(admissionId)) {
          const assignedBed = beds.find(bed => bed.id === assignment_info.bedId);
          
          if (assignedBed && !availableBeds.some(bed => bed.id === assignedBed.id)) {
            availableBeds.unshift(assignedBed); // Add at beginning
          }
        }
      }

      setBedList(availableBeds);
    } catch (error) {
      console.error("Failed to fetch beds", error);
      setBedList([]);
    }
  };
  
  const fetchDoctorsForDepartment = async (departmentId) => {
    try {
      // Use the new API that searches in multiple department fields
      const doctors = await getDoctorsByDepartmentIdMultiple(departmentId);
      console.log("Fetched doctors with multiple departments:", doctors);
      setDoctorList(Array.isArray(doctors) ? doctors : []);
    } catch (error) {
      console.log("Error fetching doctors:", error);
      // Fallback to the old API if the new one fails
      try {
        const fallbackDoctors = await getDoctorByDepartmentId(departmentId);
        console.log("Fallback doctors:", fallbackDoctors);
        setDoctorList(Array.isArray(fallbackDoctors) ? fallbackDoctors : []);
      } catch (fallbackError) {
        console.log("Fallback also failed:", fallbackError);
        setDoctorList([]);
      }
    }
  };

  const formik = useFormik({
    initialValues: {
      patientName: patient_info?.name || "",
      departmentId: assignment_info?.departmentId || "",
      doctorId: assignment_info?.doctorId || "",
      wardId: assignment_info?.wardId || "",
      bedId: assignment_info?.bedId || "",
      nurseId: assignment_info?.nurseId
        ? assignment_info.nurseId.split(",").map((id) => Number(id.trim()))
      : [],
      admissionId: assignment_info?.admissionId || "",
      admissionDate: assignment_info?.admissionDate
        ? dayjs(new Date(
            assignment_info.admissionDate[0], // year
            assignment_info.admissionDate[1] - 1, // month (subtract 1 because backend is 1-indexed, JS Date is 0-indexed)
            assignment_info.admissionDate[2] || 1, // day
            assignment_info.admissionDate[3] || 0, // hour
            assignment_info.admissionDate[4] || 0, // minute
            assignment_info.admissionDate[5] || 0  // second
          ))
        : null,
    },
    enableReinitialize: true,
   validationSchema: validationSchema(isStaff),
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      setSubmitting(true);
      try {
        // Validate bed availability before saving assignment
        // Re-fetch beds to ensure we have the latest bed status
        // Only check if bed is different from current assignment (for updates)
        const isBedChanged = !assignment_info || assignment_info.bedId !== values.bedId;
        
        if (isBedChanged) {
          try {
            const beds = await getBedsByWard(values.wardId);
            const selectedBed = beds.find(bed => bed.id === values.bedId);
            
            if (!selectedBed) {
              alert("Selected bed not found. Please select a different bed.");
              setSubmitting(false);
              return;
            }
            
            // Check if bed is already occupied
            // Only allow if it's the same bed assigned to the current admission
            if (selectedBed.isOccupied === true) {
              // Check if this bed is assigned to the current admission
              const isAssignedToCurrentAdmission = assignment_info?.bedId === values.bedId && 
                                                    assignment_info?.admissionId && 
                                                    Number(assignment_info.admissionId) === Number(admissionId);
              
              if (!isAssignedToCurrentAdmission) {
                alert("This bed is already occupied by another patient. Please select an available bed.");
                formik.setFieldError("bedId", "This bed is already occupied by another patient");
                setSubmitting(false);
                return;
              }
            }
          } catch (bedCheckError) {
            console.error("Error checking bed availability:", bedCheckError);
            alert("Failed to verify bed availability. Please try again.");
            setSubmitting(false);
            return;
          }
        }
        
        const payload = {
          patientId: patientId,
          departmentId: values.departmentId,
          doctorId: values.doctorId,
          nurseId: values.nurseId.join(","),
          wardId: values.wardId,
          bedId: values.bedId,
          admissionDate: dayjs(values.admissionDate).format("YYYY-MM-DDTHH:mm:ss"),
          expectedDischargeDate: "",
          dischargedAt: values.dischargedAt
            ? dayjs(values.dischargedAt).format("YYYY-MM-DDTHH:mm:ss")
            : null,
          status: values.status,
          admissionId: admissionId,
        };
        // if (assignment_info != null) {
        //   console.log('assignment_info', assignment_info); 
        //   alert('1');
        //   payload.id = assignment_info.id;
        //   await updateAssignment(assignment_info.id, payload);
        //   alert("Patient assignment updated successfully");
        // } else {
        //   alert('2');
        //   await saveAssignment(payload);
        //   alert("Patient assignment saved successfully");
        // }
        if (assignment_info && assignment_info.id) {
          // Refetch assignment to ensure we have the latest ID before updating
          let currentAssignment = assignment_info;
          try {
            const refreshedAssignment = await getAssignmentByAdmissionId(admissionId);
            let assignment = null;
            if (Array.isArray(refreshedAssignment)) {
              if (refreshedAssignment.length > 0) {
                const sorted = [...refreshedAssignment].sort((a, b) => {
                  if (a.createdAt && b.createdAt) {
                    return new Date(b.createdAt) - new Date(a.createdAt);
                  }
                  return (b.id || 0) - (a.id || 0);
                });
                assignment = sorted[0];
              }
            } else if (refreshedAssignment) {
              assignment = refreshedAssignment;
            }
            
            if (assignment && assignment.id) {
              currentAssignment = assignment;
            }
          } catch (refetchError) {
            console.warn("Failed to refetch assignment, using cached assignment:", refetchError);
            // Continue with cached assignment
          }
          
          console.log("Updating assignment:", currentAssignment);
          payload.id = currentAssignment.id;
          
          // Handle bed status updates when bed is changed
          const oldBedId = currentAssignment.bedId;
          const newBedId = values.bedId;
          
          await updateAssignment(currentAssignment.id, payload);
          
          // Update bed statuses: free old bed, occupy new bed
          if (oldBedId !== newBedId) {
            try {
              // Free the old bed
              if (oldBedId) {
                await updateBed(oldBedId, { isOccupied: false });
                console.log(`[patientAssignment] Bed ${oldBedId} freed`);
              }
              // Mark new bed as occupied
              await updateBed(newBedId, { isOccupied: true });
              console.log(`[patientAssignment] Bed ${newBedId} marked as occupied`);
            } catch (bedUpdateError) {
              console.error("Failed to update bed status:", bedUpdateError);
              alert("Assignment updated but bed status update failed. Please verify bed status manually.");
            }
          }
          
          alert("Patient assignment updated successfully");
        } else {
          console.log("Saving new assignment. No existing assignment_info or ID.");
          await saveAssignment(payload);

          await syncPatientTypeForWorkflow(patientId, "IN");
          
          // Update bed status to occupied after successful assignment
          try {
            await updateBed(values.bedId, { isOccupied: true });
            console.log(`[patientAssignment] Bed ${values.bedId} marked as occupied`);
          } catch (bedUpdateError) {
            console.error("Failed to update bed status:", bedUpdateError);
            alert("Assignment saved but bed status update failed. Please verify bed status manually.");
          }
          
          alert("Patient assignment saved successfully");
        }
        resetForm();
        onClose();
      } catch (error) {
        alert("Assignment failed. Please try again.");
        setSubmitting(false);
      }
    },
  });
  
  const handleClose = () => {
  formik.resetForm();
  formik.setSubmitting(false);
  setAssignmentInfo(null);   // ✅ clear old API data
  setDoctorList([]);
  setBedList([]);
  setFilteredStaffList([]);

  onClose();                 // close modal
};


  return (
    <BasicModal open={open} onClose={handleClose} sx={COMPACT_FORM_MODAL_SX}>
      <Box>
        <Typography
          variant="h5"
          sx={{ fontSize: "1.5rem", fontWeight: "bold", textAlign: "center" }}
          gutterBottom
        >
          Patient Reassignment
        </Typography>
        <form onSubmit={formik.handleSubmit} noValidate>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                select
                fullWidth
                name="departmentId"
                label="Department"
                value={formik.values.departmentId}
                onChange={(e) => {
                  const departmentId = e.target.value;
                  formik.setFieldValue("departmentId", departmentId);
                  fetchDoctorsForDepartment(departmentId);
                  formik.setFieldValue("doctorId", "");
                  const filtered = staffList.filter(
                    (staff) => staff.departmentId === departmentId
                  );
                  setFilteredStaffList(filtered);
                  formik.setFieldValue("nurseId", []);
                }}
                  disabled={isStaff}   
                error={Boolean(formik.errors.departmentId)}
                helperText={formik.errors.departmentId}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      style: { maxHeight: 200, overflowY: "auto" },
                    },
                    anchorOrigin: { vertical: "bottom", horizontal: "left" },
                    transformOrigin: { vertical: "top", horizontal: "left" },
                    getContentAnchorEl: null,
                  },
                }}
              >
                {visibleDepartments.map((dept) => (
                  <MenuItem key={dept.id} value={dept.id}>
                    {dept.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            
            <Grid item xs={6}>
              <TextField
                select
                fullWidth
                name="doctorId"
                label="Doctor"
                value={formik.values.doctorId}
                onChange={formik.handleChange}
                error={Boolean(formik.errors.doctorId)}
                helperText={
                  doctorList.length === 0
                    ? "No doctors available for this department"
                    : formik.errors.doctorId
                }
                // disabled={doctorList.length === 0}
              disabled={doctorList.length === 0 || isStaff}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      style: { maxHeight: 200, overflowY: "auto" },
                    },
                    anchorOrigin: { vertical: "bottom", horizontal: "left" },
                    transformOrigin: { vertical: "top", horizontal: "left" },
                    getContentAnchorEl: null,
                  },
                }}
              >
                {/* {doctorList.map((doc) => (
                  <MenuItem key={doc.userId} value={doc.userId}>
                    {doc.name}
                  </MenuItem>
                ))} */}
                {doctorList.map((doc) => (
                  <MenuItem key={doc.id} value={doc.id}>
                    {doc.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={6}>
              <TextField
                select
                fullWidth
                name="wardId"
                label="Ward"
                value={formik.values.wardId}
                onChange={(e) => {
                  const wardId = e.target.value;
                  formik.setFieldValue("wardId", wardId);
                  formik.setFieldTouched("wardId", true);
                  fetchBedsForWard(wardId);
                }}
                onBlur={formik.handleBlur}
                error={formik.touched.wardId && Boolean(formik.errors.wardId)}
                helperText={formik.touched.wardId && formik.errors.wardId}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      style: {
                        maxHeight: 200,
                        overflowY: "auto",
                      },
                    },
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    getContentAnchorEl: null,
                  },
                }}
              >
                {wardList.map((ward) => (
                  <MenuItem key={ward.id} value={ward.id}>
                    {ward.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={6}>
              <TextField
                select
                fullWidth
                name="bedId"
                label="Bed"
                value={formik.values.bedId}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.bedId && Boolean(formik.errors.bedId)}
                helperText={formik.touched.bedId && formik.errors.bedId}
              >
                {bedList.length === 0 ? (
                  <MenuItem disabled>No beds available</MenuItem>
                ) : (
                  bedList.map((bed) => (
                    <MenuItem key={bed.id} value={bed.id}>
                      {bed.bedNumber}
                    </MenuItem>
                  ))
                )}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Assign Staff"
                name="nurseId"
                SelectProps={{
                  multiple: true,
                  value: formik.values.nurseId,
                  onChange: formik.handleChange,
                  renderValue: (selected) =>
                    filteredStaffList
                      .filter((staff) => selected.includes(staff.id))
                      .map((staff) => staff.name)
                      .join(", "),
                  MenuProps: {
                    PaperProps: {
                      style: { maxHeight: 200, overflowY: "auto" },
                    },
                    anchorOrigin: { vertical: "bottom", horizontal: "left" },
                    transformOrigin: { vertical: "top", horizontal: "left" },
                    getContentAnchorEl: null,
                  },
                }}
                error={Boolean(formik.errors.nurseId)}
                helperText={formik.errors.nurseId}
                disabled={filteredStaffList.length === 0}
              >
                {filteredStaffList.length > 0 ? (
                  filteredStaffList.map((staff) => (
                    <MenuItem key={staff.id} value={staff.id}>
                      {staff.name}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>No staff available</MenuItem>
                )}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Assignment Date"
                  value={formik.values.admissionDate}
                  onChange={(value) =>
                    formik.setFieldValue("admissionDate", value)
                  }
                  onBlur={() =>
                    formik.setFieldTouched("admissionDate", true)
                  }
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error:
                        formik.touched.admissionDate &&
                        Boolean(formik.errors.admissionDate),
                      helperText:
                        formik.touched.admissionDate &&
                        formik.errors.admissionDate,
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12}>
              <FormModalActions
                onCancel={handleClose}
                submitType="submit"
                submitLabel="Assign"
                submitDisabled={formik.isSubmitting}
                loading={formik.isSubmitting}
              />
            </Grid>
          </Grid>
        </form>
      </Box>
    </BasicModal>
  );
};

export default AssignPatientForm;